import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';

import {saveStatusUrl, loadStatusUrl} from 'llm/deep-research/DeepResearchChatStorage';
import {
  LLM_ROLES,
  ProgressDataResponse,
  SendDeepResearchMessageResponse,
  initiateDeepResearchMessage,
  pollDeepResearchMessage,
} from 'llm/deep-research/DeepResearchDAO';
import {
  DeepResearchMessageComponent,
  DeepResearchMessage,
} from 'llm/deep-research/messages/DeepResearchMessage';
import {
  ProgressDeepResearchMessage,
  ProgressData,
  ProgressStatus,
} from 'llm/deep-research/messages/DeepResearchProgress';
import {ReportDeepResearchMessage} from 'llm/deep-research/messages/DeepResearchReport';
import {DeepResearchMessageType} from 'llm/deep-research/types';
import {convertProgressDataResponseToProgressData} from 'llm/deep-research/utils';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as logging from 'util/Logging';
import {assertNever} from 'util/Types';

import {UserDeepResearchMessage} from './messages/DeepResearchUserMessage';

// Constants
const POLLING_TIMEOUT_MINUTES = 30;
const POLLING_TIMEOUT_MS = POLLING_TIMEOUT_MINUTES * 60 * 1000;

export interface DeepResearchMessageThreadControls {
  messages: Array<DeepResearchMessage>;
  progressData: ProgressData | null;
  chatId: string | undefined;
  isSubmitting: boolean;
  sendMessage: (
    messageContent: string,
    publicDataEnabled: boolean,
    emailNotificationEnabled: boolean
  ) => Promise<void>;
  clearChat: () => void;
}

// Helper function to transform API messages to UI messages
function transformMessagesFromProgressData(
  progressDataResponse: ProgressDataResponse
): Array<DeepResearchMessage> {
  const userFacingConversationId = progressDataResponse.chatMetadata.userFacingNode.conversationId;
  const userFacingConversation = progressDataResponse.conversationHistories[userFacingConversationId];

  if (!userFacingConversation) {
    return [];
  }

  return userFacingConversation.messages
    .map((message) => {
      // Tool calls and results are shown in separate progress updates so just find the text block
      const textBlock = message.messageBlocks.find((block) => block.type === 'text');
      if (textBlock) {
        if (message.role === LLM_ROLES.USER) {
          return {
            type: DeepResearchMessageType.USER_MESSAGE,
            content: textBlock.value.text,
          };
        } else if (message.role === LLM_ROLES.ASSISTANT) {
          return {
            type: DeepResearchMessageType.SYSTEM_REPORT,
            content: textBlock.value.text,
          };
        }
      }
      return null;
    })
    .filter(
      (
        message: UserDeepResearchMessage | ReportDeepResearchMessage | null
      ): message is UserDeepResearchMessage | ReportDeepResearchMessage => message != null
    );
}

// Helper function to determine if the conversation is complete
function isConversationComplete(messages: Array<DeepResearchMessage>): boolean {
  return messages.length > 0 && messages[messages.length - 1].type === DeepResearchMessageType.SYSTEM_REPORT;
}

// Helper function to determine if this is a follow-up scenario
function isFollowUpScenario(messages: Array<DeepResearchMessage>): boolean {
  // If there are any completed system reports, this indicates follow-up questions
  return messages.some((message) => message.type === DeepResearchMessageType.SYSTEM_REPORT);
}

// Helper function to determine status and error message
function determineStatusAndError(
  progressDataResponse: ProgressDataResponse,
  result: SendDeepResearchMessageResponse | null,
  messages: Array<DeepResearchMessage>
): {status: ProgressStatus; errorMessage: string | null} {
  // Check if conversation is complete based on messages
  const errorMessage = progressDataResponse.chatMetadata.errorMessage ?? null;
  if (errorMessage) {
    return {status: ProgressStatus.FAILED, errorMessage};
  }
  if (isConversationComplete(messages)) {
    return {status: ProgressStatus.COMPLETED, errorMessage: null};
  }

  // Handle result-based status
  if (result) {
    switch (result.type) {
      case 'completed':
        logging.warn('[llm] Deep research celery task completed but persisted status is not completed', {
          extra: {progressDataResponse, result, messages},
        });
        return {status: ProgressStatus.COMPLETED, errorMessage: null};
      case 'failed':
        return {status: ProgressStatus.FAILED, errorMessage: result.errorMessage};
      default:
        throw new Error(`Unknown result type: ${assertNever(result).type}`);
    }
  }

  // Check for timeout - but skip timeout error for follow-up questions
  const isTimedOut =
    new Date(progressDataResponse.chatMetadata.modifiedAt) < new Date(Date.now() - POLLING_TIMEOUT_MS);

  if (isTimedOut && !isFollowUpScenario(messages)) {
    return {
      status: ProgressStatus.FAILED,
      errorMessage: 'Deep research is taking longer than expected.',
    };
  }

  return {status: ProgressStatus.IN_PROGRESS, errorMessage: null};
}

export function useDeepResearchMessageThread({
  chatId,
  onCreateChat,
  onRequestNewChat,
}: {
  chatId: string | null;
  onCreateChat: (chatId: string) => void;
  onRequestNewChat: () => void;
}): DeepResearchMessageThreadControls {
  // Create a ref to store the AbortController
  const initialStatusUrl = loadStatusUrl(chatId);

  const [messages, setMessages] = useState<Array<DeepResearchMessage>>([]);
  const [statusUrl, setStatusUrl] = useState<string | undefined>(initialStatusUrl);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  interface UpdateProgressData {
    newProgressData?: ProgressData;
    status?: ProgressStatus;
    errorMessage?: string | null;
  }

  const updateProgressData = useCallback(({newProgressData, status, errorMessage}: UpdateProgressData) => {
    // Update progress data
    if (newProgressData) {
      setProgressData(newProgressData);
    }

    if (status) {
      // Update or add progress message
      setMessages((prevMessages) => {
        const progressMessageIndex = prevMessages.findLastIndex(
          (msg) => msg.type === DeepResearchMessageType.PROGRESS
        );

        const updatedMessages = [...prevMessages];
        const progressMessage: ProgressDeepResearchMessage = {
          type: DeepResearchMessageType.PROGRESS,
          status,
          errorMessage: errorMessage || undefined,
        };

        if (progressMessageIndex === -1) {
          // Add new progress message
          updatedMessages.push(progressMessage);
        } else {
          // Update existing progress message
          updatedMessages[progressMessageIndex] = progressMessage;
        }
        return updatedMessages;
      });
    }
  }, []);

  // Simplified polling callback
  const handlePollingProgress = useCallback(
    (progressDataResponse: ProgressDataResponse, result: SendDeepResearchMessageResponse | null): boolean => {
      // Transform messages from progress data
      const displayMessages = transformMessagesFromProgressData(progressDataResponse);

      // Determine status and error message
      const {status, errorMessage} = determineStatusAndError(progressDataResponse, result, displayMessages);

      // Update messages
      setMessages(displayMessages);

      // Update progress data
      updateProgressData({
        newProgressData: convertProgressDataResponseToProgressData(progressDataResponse),
        status,
        errorMessage,
      });

      // Update submitting state
      const shouldContinuePolling = status === ProgressStatus.IN_PROGRESS;
      const shouldContinueSubmitting = shouldContinuePolling || status === ProgressStatus.FAILED;
      setIsSubmitting(shouldContinueSubmitting);

      return shouldContinuePolling;
    },
    [updateProgressData]
  );

  // Poll for the result and set the messages
  useEffect(() => {
    const abortController = new AbortController();
    if (chatId) {
      pollDeepResearchMessage(chatId, statusUrl ?? null, abortController.signal, handlePollingProgress).catch(
        (error) => {
          // If the error is from an abort, we can safely ignore it
          if (error?.name !== 'AbortError') {
            throw error;
          }
        }
      );
    } else {
      setMessages([]);
      setProgressData(null);
      setIsSubmitting(false);
    }
    return () => {
      abortController.abort();
    };
  }, [chatId, statusUrl, handlePollingProgress]);

  const sendMessage = useCallback(
    async (messageContent: string, publicDataEnabled: boolean, emailNotificationEnabled: boolean) => {
      const trimmedMessage = messageContent.trim();

      const userMessage: DeepResearchMessage = {
        type: DeepResearchMessageType.USER_MESSAGE,
        content: trimmedMessage,
      };

      setMessages((prevMessages) => [...prevMessages, userMessage]);
      updateProgressData({
        newProgressData: {numSources: 0, historyThread: []},
        status: ProgressStatus.STARTED,
      });
      setIsSubmitting(true);

      // First initiate the request to get the chatId immediately
      // TODO (BNCH-126624): Add try/catch for error handling
      const initResponse = await initiateDeepResearchMessage(
        chatId ?? undefined,
        trimmedMessage,
        publicDataEnabled,
        emailNotificationEnabled
      );

      // Store chatId as soon as we get it
      if (chatId && chatId !== initResponse.chatId) {
        logging.error('[llm] Chat ID mismatch', {
          extra: {storedChatId: chatId, responseChatId: initResponse.chatId},
        });
      }

      saveStatusUrl(initResponse.chatId, initResponse.statusUrl);

      // Set chat ID after all storage is updated
      onCreateChat(initResponse.chatId);
      setStatusUrl(initResponse.statusUrl);
    },
    [chatId, onCreateChat, updateProgressData]
  );

  const clearChat = useCallback(() => {
    // Reset states to allow new input
    onRequestNewChat();
    setStatusUrl(undefined);
  }, [onRequestNewChat]);

  return {
    messages,
    progressData,
    chatId: chatId || undefined,
    isSubmitting,
    sendMessage,
    clearChat,
  };
}

export interface DeepResearchMessageThreadProps {
  messages: Array<DeepResearchMessage>;
  progressData: ProgressData | null;
  openActivityModal: (activityData: ProgressData, selectedToolId?: string) => void;
}

export function DeepResearchMessageThread({
  messages,
  progressData,
  openActivityModal,
}: DeepResearchMessageThreadProps): JSX.Element {
  return (
    <div className={css(styles.threadContainer)}>
      {
        // TODO: It would be cleaner to rely on messages being `null` instead of empty to designate loading.
        // This assumes that a saved chat always has messages. True as of 2025-08-05.
        messages.length === 0 && <CenteredHorizontalThrobber />
      }
      {messages.map((message, index) => (
        <DeepResearchMessageComponent
          key={index}
          message={message}
          progressData={progressData}
          openActivityModal={openActivityModal}
        />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  threadContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    gap: '12px',
    height: '100%',
    width: '100%',
  },
});
